// Fill and stroke boxes at the bottom of the tool bar (Illustrator's): click one to make it the active box
// (X toggles), ⇆ swaps them (Shift+X), ◩ resets to white fill / black stroke (D), ⊘ sets the active box to none (/).
import { ArrowLeftRight } from 'lucide-react';
import { useT } from '../../i18n';
import { useDesign } from '../state';
import { useDesignUi } from './designUi';
import { editLook, setTargetColor, shownAppearance, targetColor } from './look';
import { cssColor } from '../render/paint';
import { BLACK, WHITE, defaultStroke } from '../model/defaults';

export function swapFillStroke() {
  editLook((a) => {
    const f = a.fills[0]?.paint,
      s = a.strokes[0]?.paint;
    if (f && s) {
      a.fills[0]!.paint = s;
      a.strokes[0]!.paint = f;
    } else if (f) {
      a.strokes = [{ ...defaultStroke(), paint: f }];
      a.fills = [];
    } else if (s) {
      a.fills = [{ paint: s, opacity: 1, blend: 'normal' }];
      a.strokes = [];
    }
  });
}
export function defaultFillStroke() {
  editLook((a) => {
    a.fills = [{ paint: { kind: 'solid', color: WHITE }, opacity: 1, blend: 'normal' }];
    a.strokes = [defaultStroke(BLACK)];
  });
}

export function FillStrokeBoxes() {
  const t = useT();
  const doc = useDesign((st) => st.doc);
  useDesign((st) => st.version);
  useDesignUi((st) => st.selection);
  useDesignUi((st) => st.current);
  const target = useDesignUi((st) => st.target);
  const a = shownAppearance();
  const f = targetColor(a, 'fill'),
    s = targetColor(a, 'stroke');
  const none = 'linear-gradient(to top right, var(--pure-white) 45%, var(--none-slash) 47%, var(--none-slash) 53%, var(--pure-white) 55%)';
  const box = (which: 'fill' | 'stroke', c: typeof f, style: React.CSSProperties) => (
    <button
      data-testid={`box-${which}`}
      aria-label={t(which === 'fill' ? 'fillBox' : 'strokeBox')}
      aria-pressed={target === which}
      onClick={() => useDesignUi.getState().set({ target: which })}
      style={{
        position: 'absolute',
        width: 22,
        height: 22,
        padding: 0,
        background: c ? cssColor(c, doc) : none,
        border: target === which ? '2px solid var(--accent)' : '1px solid var(--swatch-border)',
        ...(which === 'stroke'
          ? { boxShadow: `inset 0 0 0 5px ${c ? cssColor(c, doc) : 'var(--pure-white)'}, inset 0 0 0 7px var(--ruler-bg)` }
          : {}),
        ...style,
      }}
    />
  );
  return (
    <div style={{ marginTop: 'auto', paddingBottom: 6, display: 'grid', justifyItems: 'center', gap: 2 }}>
      <div style={{ position: 'relative', width: 34, height: 34 }}>
        {target === 'fill' ? (
          <>
            {box('stroke', s, { right: 0, bottom: 0 })}
            {box('fill', f, { left: 0, top: 0 })}
          </>
        ) : (
          <>
            {box('fill', f, { left: 0, top: 0 })}
            {box('stroke', s, { right: 0, bottom: 0 })}
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 1 }}>
        <button
          title={`${t('swapFillStroke')} (Shift+X)`}
          aria-label={t('swapFillStroke')}
          onClick={swapFillStroke}
          style={{ padding: 0, width: 13, fontSize: 9 }}
        >
          <ArrowLeftRight className="icon" aria-hidden="true" />
        </button>
        <button
          title={`${t('defaultFillStroke')} (D)`}
          aria-label={t('defaultFillStroke')}
          onClick={defaultFillStroke}
          style={{ padding: 0, width: 13, fontSize: 9 }}
        >
          ◩
        </button>
        <button
          title={`${t('noneColor')} (/)`}
          aria-label={t('noneColor')}
          onClick={() => setTargetColor(null)}
          style={{ padding: 0, width: 13, fontSize: 9 }}
        >
          ⊘
        </button>
      </div>
    </div>
  );
}
